/*
 * These decorative thumbnail glyphs (IconUser, IconDeviceImac) are the exact Tabler
 * icons the Figma design uses. They are not in the cvent iconConstants map, so the
 * Icon atom renders them via its Tabler fallback; the lint rule is disabled here only
 * for these design-mandated thumbnail icons.
 */
/* eslint-disable goldcast/no-tabler-icon-components */
import { LayoutType } from '@goldcast/player-config/src/types/asset-types';
import Icon from '@/components/atoms/Icon';

// Pixel-perfect layout thumbnails from Figma (node 107:3983). Colors map to
// Tailwind slate tokens: tile #e2e8f0=slate-200, thumbnail bg #f1f5f9=slate-100,
// PiP overlay #cbd5e1=slate-300, icon #94a3b8=slate-400. Icons are the exact
// Tabler glyphs used in the design (video, user, device-imac, wave-sine).

const ICON_CLASS = 'text-slate-400';

function Tile({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <div className={`flex items-center justify-center rounded-[4px] bg-slate-200 ${className}`}>{children}</div>;
}

function OriginalThumbnail() {
  return (
    <div className="flex h-[56px] w-full items-center justify-center overflow-hidden rounded-[6px] bg-slate-200">
      <Icon name="IconVideo" size={20} className={ICON_CLASS} />
    </div>
  );
}

function SharedFillThumbnail() {
  return (
    <div className="flex h-[56px] w-full items-center justify-between overflow-hidden rounded-[6px] bg-slate-100 p-[2px]">
      <div className="flex h-full w-[38%] flex-col gap-[2px]">
        <Tile className="flex-1">
          <Icon name="IconUser" size={14} className={ICON_CLASS} />
        </Tile>
        <Tile className="flex-1">
          <Icon name="IconUser" size={14} className={ICON_CLASS} />
        </Tile>
      </div>
      <Tile className="h-full w-[59%] p-[4px]">
        <Icon name="IconDeviceImac" size={16} className={ICON_CLASS} />
      </Tile>
    </div>
  );
}

function FilmStripThumbnail() {
  return (
    <div className="flex h-[56px] w-full items-center justify-between overflow-hidden rounded-[6px] bg-slate-100 px-[4px] py-[3px]">
      <div className="flex h-full w-[18%] flex-col gap-[2px]">
        <div className="flex-1 rounded-[4px] bg-slate-200" />
        <div className="flex-1 rounded-[4px] bg-slate-200" />
        <div className="flex-1 rounded-[4px] bg-slate-200" />
      </div>
      <Tile className="h-full w-[76%] p-[4px]">
        <Icon name="IconDeviceImac" size={16} className={ICON_CLASS} />
      </Tile>
    </div>
  );
}

function PipThumbnail() {
  return (
    <div className="relative flex h-[56px] w-full items-center overflow-hidden rounded-[6px] bg-slate-100 p-[2px]">
      <Tile className="h-full flex-1 p-[4px]">
        <Icon name="IconDeviceImac" size={16} className={ICON_CLASS} />
      </Tile>
      <div className="absolute bottom-[4px] right-[4px] flex h-[23px] w-[32px] items-center justify-center rounded-[4px] bg-slate-300">
        <Icon name="IconUser" size={14} className={ICON_CLASS} />
      </div>
    </div>
  );
}

function SingleTileThumbnail({ icon }: { icon: 'IconDeviceImac' | 'IconWaveSine' }) {
  return (
    <div className="flex h-[56px] w-full items-center overflow-hidden rounded-[6px] bg-slate-100 p-[2px]">
      <Tile className="h-full flex-1 p-[4px]">
        <Icon name={icon} size={20} className={ICON_CLASS} />
      </Tile>
    </div>
  );
}

export default function LayoutThumbnail({ layout }: { layout: LayoutType }) {
  switch (layout) {
    case 'DEFAULT':
      return <OriginalThumbnail />;
    case 'GRID':
      return <SharedFillThumbnail />;
    case 'FOCUS':
      return <FilmStripThumbnail />;
    case 'PIP':
      return <PipThumbnail />;
    case 'AUDIOGRAM':
      return <SingleTileThumbnail icon="IconWaveSine" />;
    case 'SPEAKER':
    default:
      return <SingleTileThumbnail icon="IconDeviceImac" />;
  }
}
